import { useEffect, useRef, useState } from 'react';
import {
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useSaveTranscriptSegment } from '../api/useMutations';
import type { CallParticipant, TranscriptSegment } from '../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  segment: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  header: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'baseline',
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
  },
});

interface TranscriptTabProps {
  segments: TranscriptSegment[];
  participants: CallParticipant[];
  isEditing?: boolean;
  saveSignal?: number;
  resetSignal?: number;
  transcriptId?: string;
  callId?: number;
  meetingId?: string;
}

function formatTimestamp(value: number | string): string {
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function TranscriptTab({
  segments,
  participants,
  isEditing = false,
  saveSignal = 0,
  resetSignal = 0,
  transcriptId,
  callId = 0,
  meetingId,
}: TranscriptTabProps) {
  const styles = useStyles();
  const { t } = useTranslation();

  const userSegments = segments.filter(s => s.source === 'user');

  const participantMap = new Map(
    participants.map(p => [p.user.id, p.user.name]),
  );

  // Editable state: map of segmentId -> edited text
  const [editedSegments, setEditedSegments] = useState<Record<string, string>>({});

  // Reset on cancel
  const prevResetSignal = useRef(resetSignal);
  useEffect(() => {
    if (resetSignal !== prevResetSignal.current) {
      prevResetSignal.current = resetSignal;
      setEditedSegments({});
    }
  }, [resetSignal]);

  // Reset when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditedSegments({});
    }
  }, [isEditing]);

  // Mutation
  const saveSegment = useSaveTranscriptSegment(callId, meetingId);

  // Save ref pattern to avoid exhaustive deps issues
  const saveRef = useRef(() => {});
  saveRef.current = () => {
    if (!transcriptId) return;
    for (const [segmentId, text] of Object.entries(editedSegments)) {
      const original = segments.find(s => s.id === segmentId);
      if (original && text !== original.text) {
        saveSegment.mutate({ transcriptId, segmentId, text });
      }
    }
  };

  const prevSaveSignal = useRef(saveSignal);
  useEffect(() => {
    if (saveSignal !== prevSaveSignal.current) {
      prevSaveSignal.current = saveSignal;
      saveRef.current();
    }
  }, [saveSignal]);

  const handleSegmentChange = (segmentId: string, text: string) => {
    setEditedSegments(prev => ({ ...prev, [segmentId]: text }));
  };

  return (
    <div className={styles.container}>
      {userSegments.map(segment => (
        <div key={segment.id} className={styles.segment}>
          <div className={styles.header}>
            <Text weight="semibold" size={200}>
              {participantMap.get(segment.userId) ?? t('unknown')}
            </Text>
            <Text size={100} className={styles.timestamp}>
              {formatTimestamp(segment.timestamp)}
            </Text>
          </div>
          {isEditing ? (
            <Textarea
              value={editedSegments[segment.id] ?? segment.text}
              onChange={(_, data) => handleSegmentChange(segment.id, data.value)}
              resize="vertical"
              rows={1}
              size="small"
            />
          ) : (
            <Text size={300}>{segment.text}</Text>
          )}
        </div>
      ))}
    </div>
  );
}
